// SPEC-webplanner-054 / GAP-WEBPLANNER-078 — the single definition of "is this
// timestamp real, or is it a default-DateTime sentinel?".
//
// WHY THIS FILE EXISTS. `Meeting.EndTime` / `StartTime` are NON-nullable
// `DateTime` server-side, so a row persisted without one materializes as
// `0001-01-01T00:00:00` rather than null. That value is a perfectly valid
// JS Date, so the usual `!v` / `isNaN(d.getTime())` guards let it through and
// consumers render it as if the platform had observed something. GAP-078 is
// exactly that: the empty-room report printed "stayed live until 1 Jan, 00:00"
// on a session that opened 5 Jul.
//
// The pre-1970 test predates this file — it was written twice, in
// `qualification-review.component.ts` (hasRealEndTime/hasRealStartTime) and
// again in `session-schedule.component.ts`, whose comment says it deliberately
// mirrored the first "so the two 'is this end usable' checks in the codebase
// never drift apart". The empty-room report was the third such check and the one
// that drifted. This file is that comment's wish made structural: one definition,
// three consumers, and SPEC-054 AC9 greps to keep a fourth copy from appearing.
//
// The threshold is `> 1970` because that is what the two original sites use.
// Do NOT "improve" it to 1900 or `getTime() > 0` — a fourth subtly-different
// variant is the precise failure this consolidation exists to prevent.
//
// Pure functions, no Angular, no DI — same shape as format-expires-in.ts and
// session-display-state.ts.

/** Earliest year we treat as a genuine timestamp. Anything at or below this is
 *  a default/epoch sentinel, not an observation. */
const SENTINEL_YEAR_CEILING = 1970;

/**
 * True when `v` parses to a Date that represents a real, observed moment.
 *
 * False for: null / undefined / empty string, unparseable strings, the
 * `DateTime.MinValue` sentinel (`0001-01-01T00:00:00`, with or without `Z`),
 * and the unix epoch.
 */
export function isRealTimestamp(v: unknown): boolean {
    if (v === null || v === undefined || v === '') { return false; }
    const d = new Date(v as string | number | Date);
    if (isNaN(d.getTime())) { return false; }
    return d.getFullYear() > SENTINEL_YEAR_CEILING;
}

/**
 * The parsed Date when `v` is a real timestamp, else null.
 *
 * Use this at call sites that want a Date; use {@link isRealTimestamp} at call
 * sites that only need the boolean (e.g. deciding whether to render a row).
 */
export function realDateOrNull(v: unknown): Date | null {
    return isRealTimestamp(v) ? new Date(v as string | number | Date) : null;
}
